import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Check, X, Plus, Loader2 } from 'lucide-react';
import { lookupAdminApi } from '../services/lookupAdminApi';
import type { ClinicCategoryType, ClinicCapabilityType } from '@features/clinics/types/clinic';
import type {
  CreateCategoryTypeRequest,
  UpdateCategoryTypeRequest,
  CreateCapabilityTypeRequest,
  UpdateCapabilityTypeRequest,
} from '../services/lookupAdminApi';
import RequirePermission from '@shared/components/RequirePermission';
import './LookupAdminPage.css';

/* ─── Helpers ──────────────────────────────────────────── */

/** Generate a key from a name: lowercase, spaces to underscores, strip non-alphanumeric */
function generateKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

/* ─── Category Row Editing State ───────────────────────── */

interface CategoryEditState {
  name: string;
  displayOrder: number;
  isActive: boolean;
}

interface CapabilityEditState {
  name: string;
  key: string;
  displayOrder: number;
  isActive: boolean;
}

/* ─── Component ────────────────────────────────────────── */

export default function LookupAdminPage() {
  const queryClient = useQueryClient();

  // ─── Data queries ───────────────────────────────────────
  const { data: categoryTypes = [], isLoading: catLoading } = useQuery({
    queryKey: ['adminCategoryTypes'],
    queryFn: lookupAdminApi.getAllCategoryTypes,
  });

  const { data: capabilityTypes = [], isLoading: capLoading } = useQuery({
    queryKey: ['adminCapabilityTypes'],
    queryFn: lookupAdminApi.getAllCapabilityTypes,
  });

  // ─── Category mutations ─────────────────────────────────
  const createCategoryMutation = useMutation({
    mutationFn: (req: CreateCategoryTypeRequest) => lookupAdminApi.createCategoryType(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCategoryTypes'] });
      queryClient.invalidateQueries({ queryKey: ['categoryTypes'] });
      setCatAdding(false);
      setCatNewRow({ name: '', displayOrder: 0 });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateCategoryTypeRequest }) =>
      lookupAdminApi.updateCategoryType(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCategoryTypes'] });
      queryClient.invalidateQueries({ queryKey: ['categoryTypes'] });
      setCatEditingId(null);
    },
  });

  // ─── Capability mutations ───────────────────────────────
  const createCapabilityMutation = useMutation({
    mutationFn: (req: CreateCapabilityTypeRequest) => lookupAdminApi.createCapabilityType(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCapabilityTypes'] });
      queryClient.invalidateQueries({ queryKey: ['capabilityTypes'] });
      setCapAdding(false);
      setCapNewRow({ name: '', key: '', displayOrder: 0 });
    },
  });

  const updateCapabilityMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateCapabilityTypeRequest }) =>
      lookupAdminApi.updateCapabilityType(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCapabilityTypes'] });
      queryClient.invalidateQueries({ queryKey: ['capabilityTypes'] });
      setCapEditingId(null);
    },
  });

  // ─── Category editing state ─────────────────────────────
  const [catEditingId, setCatEditingId] = useState<number | null>(null);
  const [catEditState, setCatEditState] = useState<CategoryEditState>({
    name: '',
    displayOrder: 0,
    isActive: true,
  });
  const [catAdding, setCatAdding] = useState(false);
  const [catNewRow, setCatNewRow] = useState<{ name: string; displayOrder: number }>({
    name: '',
    displayOrder: 0,
  });

  // ─── Capability editing state ───────────────────────────
  const [capEditingId, setCapEditingId] = useState<number | null>(null);
  const [capEditState, setCapEditState] = useState<CapabilityEditState>({
    name: '',
    key: '',
    displayOrder: 0,
    isActive: true,
  });
  const [capAdding, setCapAdding] = useState(false);
  const [capNewRow, setCapNewRow] = useState<{ name: string; key: string; displayOrder: number }>({
    name: '',
    key: '',
    displayOrder: 0,
  });

  // ─── Category handlers ─────────────────────────────────
  const startCategoryEdit = (cat: ClinicCategoryType) => {
    setCatEditingId(cat.id);
    setCatEditState({ name: cat.name, displayOrder: cat.displayOrder, isActive: cat.isActive });
  };

  const cancelCategoryEdit = () => {
    setCatEditingId(null);
  };

  const saveCategoryEdit = () => {
    if (catEditingId === null) return;
    updateCategoryMutation.mutate({
      id: catEditingId,
      request: {
        name: catEditState.name,
        displayOrder: catEditState.displayOrder,
        isActive: catEditState.isActive,
      },
    });
  };

  const startCategoryAdd = () => {
    const maxOrder = categoryTypes.length > 0
      ? Math.max(...categoryTypes.map((c) => c.displayOrder))
      : 0;
    setCatNewRow({ name: '', displayOrder: maxOrder + 1 });
    setCatAdding(true);
  };

  const cancelCategoryAdd = () => {
    setCatAdding(false);
    setCatNewRow({ name: '', displayOrder: 0 });
  };

  const saveCategoryAdd = () => {
    if (!catNewRow.name.trim()) return;
    createCategoryMutation.mutate({
      name: catNewRow.name.trim(),
      displayOrder: catNewRow.displayOrder,
    });
  };

  // ─── Capability handlers ───────────────────────────────
  const startCapabilityEdit = (cap: ClinicCapabilityType) => {
    setCapEditingId(cap.id);
    setCapEditState({
      name: cap.name,
      key: cap.key,
      displayOrder: cap.displayOrder,
      isActive: cap.isActive,
    });
  };

  const cancelCapabilityEdit = () => {
    setCapEditingId(null);
  };

  const saveCapabilityEdit = () => {
    if (capEditingId === null) return;
    updateCapabilityMutation.mutate({
      id: capEditingId,
      request: {
        name: capEditState.name,
        key: capEditState.key,
        displayOrder: capEditState.displayOrder,
        isActive: capEditState.isActive,
      },
    });
  };

  const startCapabilityAdd = () => {
    const maxOrder = capabilityTypes.length > 0
      ? Math.max(...capabilityTypes.map((c) => c.displayOrder))
      : 0;
    setCapNewRow({ name: '', key: '', displayOrder: maxOrder + 1 });
    setCapAdding(true);
  };

  const cancelCapabilityAdd = () => {
    setCapAdding(false);
    setCapNewRow({ name: '', key: '', displayOrder: 0 });
  };

  const saveCapabilityAdd = () => {
    if (!capNewRow.name.trim()) return;
    const key = capNewRow.key.trim() || generateKey(capNewRow.name);
    createCapabilityMutation.mutate({
      name: capNewRow.name.trim(),
      key,
      displayOrder: capNewRow.displayOrder,
    });
  };

  // ─── Render ─────────────────────────────────────────────
  return (
    <div className="lookup-admin-page">
      {/* Header */}
      <div className="lookup-admin-header">
        <h1>Lookup Tables</h1>
        <p className="lookup-admin-description">
          Manage category and capability types for clinics.
        </p>
      </div>

      {/* ─── Categories Section ─── */}
      <div className="lookup-section">
        <div className="lookup-section-header">
          <h2>Clinic Categories</h2>
          <RequirePermission permission="UserCanManageClinicCategoryTypes">
            <button
              type="button"
              className="lookup-add-btn"
              onClick={startCategoryAdd}
              disabled={catAdding}
            >
              <Plus size={14} />
              Add New
            </button>
          </RequirePermission>
        </div>

        {catLoading ? (
          <div className="lookup-loading">
            <Loader2 size={24} className="spinning" />
            <span>Loading categories...</span>
          </div>
        ) : (
          <div className="lookup-table-wrapper">
            <table className="lookup-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Display Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Add-new row */}
                {catAdding && (
                  <tr className="lookup-row-editing">
                    <td>
                      <input
                        type="text"
                        className="lookup-input"
                        value={catNewRow.name}
                        onChange={(e) => setCatNewRow({ ...catNewRow, name: e.target.value })}
                        placeholder="Category name"
                        aria-label="Category name"
                        autoFocus
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="lookup-input lookup-input-narrow"
                        value={catNewRow.displayOrder}
                        onChange={(e) =>
                          setCatNewRow({ ...catNewRow, displayOrder: parseInt(e.target.value) || 0 })
                        }
                        aria-label="Display order"
                      />
                    </td>
                    <td>
                      <span className="lookup-status-badge lookup-status-active">Active</span>
                    </td>
                    <td className="lookup-actions">
                      <button
                        type="button"
                        className="lookup-action-btn lookup-action-save"
                        onClick={saveCategoryAdd}
                        disabled={!catNewRow.name.trim() || createCategoryMutation.isPending}
                        title="Save"
                        aria-label="Save new category"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        className="lookup-action-btn lookup-action-cancel"
                        onClick={cancelCategoryAdd}
                        disabled={createCategoryMutation.isPending}
                        title="Cancel"
                        aria-label="Cancel new category"
                      >
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                )}

                {/* Existing rows */}
                {categoryTypes.map((cat) => (
                  <tr
                    key={cat.id}
                    className={
                      catEditingId === cat.id
                        ? 'lookup-row-editing'
                        : !cat.isActive
                          ? 'lookup-row-inactive'
                          : ''
                    }
                  >
                    <td>
                      {catEditingId === cat.id ? (
                        <input
                          type="text"
                          className="lookup-input"
                          value={catEditState.name}
                          onChange={(e) =>
                            setCatEditState({ ...catEditState, name: e.target.value })
                          }
                          aria-label="Category name"
                        />
                      ) : (
                        cat.name
                      )}
                    </td>
                    <td>
                      {catEditingId === cat.id ? (
                        <input
                          type="number"
                          className="lookup-input lookup-input-narrow"
                          value={catEditState.displayOrder}
                          onChange={(e) =>
                            setCatEditState({
                              ...catEditState,
                              displayOrder: parseInt(e.target.value) || 0,
                            })
                          }
                          aria-label="Display order"
                        />
                      ) : (
                        cat.displayOrder
                      )}
                    </td>
                    <td>
                      {catEditingId === cat.id ? (
                        <button
                          type="button"
                          className={`lookup-status-badge lookup-status-toggle ${
                            catEditState.isActive ? 'lookup-status-active' : 'lookup-status-inactive'
                          }`}
                          onClick={() =>
                            setCatEditState({ ...catEditState, isActive: !catEditState.isActive })
                          }
                        >
                          {catEditState.isActive ? 'Active' : 'Inactive'}
                        </button>
                      ) : (
                        <span
                          className={`lookup-status-badge ${
                            cat.isActive ? 'lookup-status-active' : 'lookup-status-inactive'
                          }`}
                        >
                          {cat.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="lookup-actions">
                      {catEditingId === cat.id ? (
                        <>
                          <button
                            type="button"
                            className="lookup-action-btn lookup-action-save"
                            onClick={saveCategoryEdit}
                            disabled={!catEditState.name.trim() || updateCategoryMutation.isPending}
                            title="Save"
                            aria-label="Save category edit"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            type="button"
                            className="lookup-action-btn lookup-action-cancel"
                            onClick={cancelCategoryEdit}
                            disabled={updateCategoryMutation.isPending}
                            title="Cancel"
                            aria-label="Cancel category edit"
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="lookup-action-btn lookup-action-edit"
                          onClick={() => startCategoryEdit(cat)}
                          title="Edit"
                          aria-label="Edit category"
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {categoryTypes.length === 0 && !catAdding && (
                  <tr>
                    <td colSpan={4} className="lookup-empty">
                      No category types defined.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Capabilities Section ─── */}
      <div className="lookup-section">
        <div className="lookup-section-header">
          <h2>Clinic Capabilities</h2>
          <RequirePermission permission="UserCanManageClinicCapabilityTypes">
            <button
              type="button"
              className="lookup-add-btn"
              onClick={startCapabilityAdd}
              disabled={capAdding}
            >
              <Plus size={14} />
              Add New
            </button>
          </RequirePermission>
        </div>

        {capLoading ? (
          <div className="lookup-loading">
            <Loader2 size={24} className="spinning" />
            <span>Loading capabilities...</span>
          </div>
        ) : (
          <div className="lookup-table-wrapper">
            <table className="lookup-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Display Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Add-new row */}
                {capAdding && (
                  <tr className="lookup-row-editing">
                    <td>
                      <input
                        type="text"
                        className="lookup-input"
                        value={capNewRow.name}
                        onChange={(e) => {
                          const newName = e.target.value;
                          setCapNewRow((prev) => ({
                            ...prev,
                            name: newName,
                            // Auto-generate key if user hasn't manually entered one
                            key: prev.key === generateKey(prev.name)
                              ? generateKey(newName)
                              : prev.key,
                          }));
                        }}
                        placeholder="Capability name"
                        aria-label="Capability name"
                        autoFocus
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="lookup-input"
                        value={capNewRow.key}
                        onChange={(e) => setCapNewRow({ ...capNewRow, key: e.target.value })}
                        placeholder="auto-generated"
                        aria-label="Capability key"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="lookup-input lookup-input-narrow"
                        value={capNewRow.displayOrder}
                        onChange={(e) =>
                          setCapNewRow({
                            ...capNewRow,
                            displayOrder: parseInt(e.target.value) || 0,
                          })
                        }
                        aria-label="Display order"
                      />
                    </td>
                    <td>
                      <span className="lookup-status-badge lookup-status-active">Active</span>
                    </td>
                    <td className="lookup-actions">
                      <button
                        type="button"
                        className="lookup-action-btn lookup-action-save"
                        onClick={saveCapabilityAdd}
                        disabled={!capNewRow.name.trim() || createCapabilityMutation.isPending}
                        title="Save"
                        aria-label="Save new capability"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        className="lookup-action-btn lookup-action-cancel"
                        onClick={cancelCapabilityAdd}
                        disabled={createCapabilityMutation.isPending}
                        title="Cancel"
                        aria-label="Cancel new capability"
                      >
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                )}

                {/* Existing rows */}
                {capabilityTypes.map((cap) => (
                  <tr
                    key={cap.id}
                    className={
                      capEditingId === cap.id
                        ? 'lookup-row-editing'
                        : !cap.isActive
                          ? 'lookup-row-inactive'
                          : ''
                    }
                  >
                    <td>
                      {capEditingId === cap.id ? (
                        <input
                          type="text"
                          className="lookup-input"
                          value={capEditState.name}
                          onChange={(e) =>
                            setCapEditState({ ...capEditState, name: e.target.value })
                          }
                          aria-label="Capability name"
                        />
                      ) : (
                        cap.name
                      )}
                    </td>
                    <td className="lookup-key-cell">
                      {capEditingId === cap.id ? (
                        <input
                          type="text"
                          className="lookup-input"
                          value={capEditState.key}
                          onChange={(e) =>
                            setCapEditState({ ...capEditState, key: e.target.value })
                          }
                          aria-label="Capability key"
                        />
                      ) : (
                        <code className="lookup-key-code">{cap.key}</code>
                      )}
                    </td>
                    <td>
                      {capEditingId === cap.id ? (
                        <input
                          type="number"
                          className="lookup-input lookup-input-narrow"
                          value={capEditState.displayOrder}
                          onChange={(e) =>
                            setCapEditState({
                              ...capEditState,
                              displayOrder: parseInt(e.target.value) || 0,
                            })
                          }
                          aria-label="Display order"
                        />
                      ) : (
                        cap.displayOrder
                      )}
                    </td>
                    <td>
                      {capEditingId === cap.id ? (
                        <button
                          type="button"
                          className={`lookup-status-badge lookup-status-toggle ${
                            capEditState.isActive ? 'lookup-status-active' : 'lookup-status-inactive'
                          }`}
                          onClick={() =>
                            setCapEditState({ ...capEditState, isActive: !capEditState.isActive })
                          }
                        >
                          {capEditState.isActive ? 'Active' : 'Inactive'}
                        </button>
                      ) : (
                        <span
                          className={`lookup-status-badge ${
                            cap.isActive ? 'lookup-status-active' : 'lookup-status-inactive'
                          }`}
                        >
                          {cap.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="lookup-actions">
                      {capEditingId === cap.id ? (
                        <>
                          <button
                            type="button"
                            className="lookup-action-btn lookup-action-save"
                            onClick={saveCapabilityEdit}
                            disabled={
                              !capEditState.name.trim() ||
                              !capEditState.key.trim() ||
                              updateCapabilityMutation.isPending
                            }
                            title="Save"
                            aria-label="Save capability edit"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            type="button"
                            className="lookup-action-btn lookup-action-cancel"
                            onClick={cancelCapabilityEdit}
                            disabled={updateCapabilityMutation.isPending}
                            title="Cancel"
                            aria-label="Cancel capability edit"
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="lookup-action-btn lookup-action-edit"
                          onClick={() => startCapabilityEdit(cap)}
                          title="Edit"
                          aria-label="Edit capability"
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {capabilityTypes.length === 0 && !capAdding && (
                  <tr>
                    <td colSpan={5} className="lookup-empty">
                      No capability types defined.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}